<template>
    <div class="input-group mb-3">
        <!-- recordings -->
        <b-pagination
            v-model="currentPage"
            :total-rows="recordings.length"
            :per-page="perPage"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
        ></b-pagination>

        <b-table
            :dark="dark"
            striped
            :items="recordingsPage"
            :fields="fields"
            sortBy="startTime"
            sortDesc
            thead-class=""
        >
            <template #cell(displayName)="row">
                <a v-if="row.item.secureUrl" v-bind:href="row.item.secureUrl">{{
                    row.item.displayName
                }}</a>
                <div v-else>{{ row.item.displayName }}</div>
                <div class="small">id: {{ row.item.recordingId }}</div>

                <div v-if="row.item.secureUrl">
                    <audio
                        v-bind:src="row.item.secureUrl"
                        controls
                        preload="none"
                    />
                </div>

                <ForceCompleteButton
                    v-if="showForceComplete(row.item)"
                    @click="forceComplete(row.item.recordingId)"
                />
            </template>

            <template #cell(startTime)="data">
                {{ formatTime(data.value) }}
            </template>

            <template #cell(dataWritten)="data">
                {{ humanFileSize(data.value) }}
            </template>

            <template #cell(actions)="row">
                <b-button 
                    v-if="showReTranscode(row.item)"
                    @click="onReTranscode(row.item)">Re-transcode</b-button>

                <b-button
                    @click="onGenerateLink(row.item)">Re-generate link</b-button>
            </template>
        </b-table>
    </div>
</template>

<script>
import api from '@/api';
import { formatDateTime, humanFileSize } from "../utils";
import ForceCompleteButton from "./ForceCompleteButton";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export default {
    components: {
        ForceCompleteButton,
    },

    name: "RecordingsList",
    props: {
        dark: { type: Boolean, default: false },
        sessionId: { type: String },
    },
    data: function () {
        return {
            recordingsTimer: null,
            recordings: [],

            perPage: 20,
            currentPage: 1,

            fields: [
                { key: "displayName" },
                { key: "startTime" },
                { key: "dataWritten" }
            ],
        };
    },
    computed: {
        recordingsPage() {
            let firstItem = (this.currentPage - 1) * this.perPage;
            let lastItem = this.currentPage * this.perPage;
            return this.recordings.slice(firstItem, lastItem);
        },
    },
    methods: {
        dateOlder(date, ms) {
            return ((Date.now() - new Date(date).getTime()) < ms);
        },

        dateNewer(date, ms) {
            return ((Date.now() - new Date(date).getTime()) > ms);
        },

        dateWithin(date, fromMs, toMs) {
            return this.dateNewer(date, toMs) && this.dateOlder(date, fromMs);
        },

        showReTranscode(recording) {
            return !this.dateNewer(recording.startTime, 7 * DAY);
        },

        showForceComplete(recording) {
            if (recording.status === "DISCONNECTED") {
                if (this.dateNewer(recording.updatedAt, 1 * MINUTE)) return true;
            }

            if (recording.status === "RECORDING") {
                if (this.dateNewer(recording.updatedAt, 5 * MINUTE)) return true;
            }
        },

        alert(s) {
            alert(`${s.sessionId}
            ${s.recordingId}`);
        },

        formatTime(t) {
            return formatDateTime(t);
        },

        humanFileSize(s) {
            return humanFileSize(s);
        },

        forceComplete(recordingId) {
            this.$store.dispatch("recorder/ForceComplete", recordingId);
            this.startPolling();
        },

        async onReTranscode(item) {
            const {sessionId, recordingId} = item;
            try {
                let result = await api.recordings.forceTranscode({sessionId, recordingId});
                let {error, message} = result;

                if (error) {
                    console.error(message);
                    return;
                }
            }
            catch (err) {
                console.error(err);
            }
        },

        async onGenerateLink(item) {
            const { sessionId, recordingId} = item;
            try {
                let result = await api.recordings.generateLink({sessionId, recordingId});
                let {error, message} = result;
                if (error) {
                    console.error(message);
                    return;
                }
            }
            catch (err) {
                console.error(err);
            }
        },

        async getRecordings() {
            try {
                let result = await api.recordings.getBySessionId(
                    this.sessionId,
                    false
                );
                let {error, message, results} = result;

                if (error) {
                    console.error(message);
                    return;
                }

                let { recordings } = results; 

                this.recordings = recordings.filter(
                    // don't show old format recordings
                    (item) => item.startTime != undefined
                );
            } catch (err) {
                console.log("Couldn't retrieve session recordings", err);
            }
        },

        poll() {
            this.getRecordings();

            let pollTime = this.$store.getters.isRecording ? 2000 : 5000;
            this.recordingsTimer = setTimeout(this.poll.bind(this), pollTime);
        },

        startPolling() {
            if (this.recordingsTimer) return;
            this.currentPage = 1;
            this.poll();
        },

        stopPolling() {
            if (this.recordingsTimer) clearTimeout(this.recordingsTimer);
        },
    },

    mounted() {
        if (this.$store.getters.isAdmin) {
            this.fields.push({key: 'actions'});
        }

        this.startPolling();
    },

    beforeDestroy() {
        this.stopPolling();
    },
};
</script>
